import { formatMediaUrl } from "./adsCampaigns";
import {
  regenerateAdTextsFromCreative,
  startDeleteAdMediaID,
} from "../actions/adsCampaigns";
import { ConfirmAlert } from "../components/Alerts/Alerts";
import { getCreativeOptimizationEntryForSlot } from "../utils/creativeOptimizationContext";
import { getUniqueID } from "../utils/numbers";
import { META, TIKTOK, LINKEDIN } from "../utils/constants";
import {
  CONFIRM_REGENERATE_TEXTS_AFTER_CREATIVE_CHANGE_TITLE,
  BUTTON_REGENERATE_TEXT,
  BUTTON_KEEP_TEXTS,
} from "../i18n/keysTranslations";

const TIKTOK_DEFAULT_MUSIC_URL =
  "https://clapp-prod.s3.amazonaws.com/Buzzly/soundtrack.mp3";

const REGENERATE_TEXTS_CONFIRM_ALERT_DELAY_MS = 1000;

const SUPPORTED_PLATFORMS = [META, TIKTOK, LINKEDIN];

const PLATFORM_MEDIA_CHANGE_RULES = {
  [META]: {
    shouldReplaceCarousel: (isCarouselAd, carousel) => isCarouselAd || carousel,
    syncDestinationUrls: true,
    syncMediaIDs: true,
    tiktokDefaultMusic: false,
    carouselIsCarouselFlag: (carousel) => carousel,
  },
  [TIKTOK]: {
    shouldReplaceCarousel: (isCarouselAd, carousel) =>
      carousel && !isCarouselAd,
    syncDestinationUrls: false,
    syncMediaIDs: false,
    tiktokDefaultMusic: true,
    carouselIsCarouselFlag: () => true,
  },
  [LINKEDIN]: {
    shouldReplaceCarousel: () => false,
    syncDestinationUrls: false,
    syncMediaIDs: false,
    tiktokDefaultMusic: false,
    carouselIsCarouselFlag: () => false,
  },
};

const getRegeneratedTextFieldConfig = (platform, isCarousel) => {
  if (platform === META) {
    return {
      fields: ["titles", "headlines", "descriptions"],
      triggerFields: ["titles", "headlines", "descriptions"],
    };
  }

  if (platform === TIKTOK) {
    return {
      fields: ["titles", "headlines", "descriptions"],
      triggerFields: ["titles"],
    };
  }

  if (platform === LINKEDIN) {
    const fields = isCarousel
      ? ["titles", "headlines"]
      : ["titles", "headlines", "descriptions"];
    return { fields, triggerFields: fields };
  }

  return { fields: [], triggerFields: [] };
};

const delayMs = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const isCarouselContent = (content) =>
  content?.isCarousel === true &&
  Array.isArray(content?.carouselImages) &&
  content.carouselImages.length > 0;

/** @see creativeOptimizationContext — shared slot lookup for previews and this service */
export const getMatchedCreativeEntryForAdSlot = ({
  creativeOptimizations,
  basicPath,
}) =>
  getCreativeOptimizationEntryForSlot({
    creativeOptimizations,
    formPath: basicPath,
    field: "mediaUrls",
  });

const clearCreativeOptimizationIfMatched = ({
  creativeOptimizations,
  basicPath,
  onRemoveCreativeOptimization,
}) => {
  const matched = getMatchedCreativeEntryForAdSlot({
    creativeOptimizations,
    basicPath,
  });
  if (matched?.slotKey) {
    onRemoveCreativeOptimization(matched.slotKey);
  }
};

const deleteAllMediaIDs = async ({ dispatch, adID, getValues, basicPath }) => {
  const currentMediaIDs = getValues(`${basicPath}.mediaIDs`) || [];
  const deleteTasks = currentMediaIDs
    .map((mediaID, index) =>
      mediaID
        ? dispatch(
            startDeleteAdMediaID({
              adID,
              index,
            })
          )
        : null
    )
    .filter(Boolean);

  if (deleteTasks.length > 0) {
    await Promise.all(deleteTasks);
  }
};

const buildCarouselItemsFromContent = (content, carousel) => {
  if (carousel) {
    return content.carouselImages.map((carouselImage) => ({
      ...carouselImage,
      url: carouselImage.url || carouselImage,
      from: content.from || "upload",
    }));
  }

  return [content];
};

const applyCarouselCreativeChange = async ({
  content,
  carousel,
  dispatch,
  adID,
  basicPath,
  getValues,
  setValue,
  watch,
  rules,
}) => {
  await deleteAllMediaIDs({ dispatch, adID, getValues, basicPath });

  const contentsToApply = buildCarouselItemsFromContent(content, carousel);
  const newMediaUrls = [];
  const newMediaData = [];
  const newMediaIDs = [];

  contentsToApply.forEach((item) => {
    const formatted = formatMediaUrl(item);
    newMediaUrls.push(formatted.url);
    newMediaData.push(formatted.mediaData);
    if (rules.syncMediaIDs) {
      newMediaIDs.push("");
    }
  });

  setValue(`${basicPath}.mediaUrls`, newMediaUrls, {
    shouldValidate: true,
  });
  setValue(`${basicPath}.mediaData`, newMediaData);

  if (rules.syncMediaIDs) {
    setValue(`${basicPath}.mediaIDs`, newMediaIDs);
  }

  if (rules.syncDestinationUrls) {
    const currentDestinationUrls =
      getValues(`${basicPath}.destinationUrls`) || [];
    const destinationUrl = currentDestinationUrls[0]?.value || "";
    const newDestinationUrls = newMediaUrls.map(() => ({
      value: destinationUrl,
      id: getUniqueID(),
    }));
    setValue(`${basicPath}.destinationUrls`, newDestinationUrls);
  }

  setValue(`${basicPath}.isCarousel`, rules.carouselIsCarouselFlag(carousel));

  if (rules.tiktokDefaultMusic) {
    const musicUrl = watch(`${basicPath}.musicUrl`);
    if (!musicUrl) {
      setValue(`${basicPath}.musicUrl`, TIKTOK_DEFAULT_MUSIC_URL);
      setValue(`${basicPath}.musicID`, null);
    }
  }

  return {
    mediaUrls: newMediaUrls,
    isCarousel: rules.carouselIsCarouselFlag(carousel),
  };
};

const applySingleSlotCreativeChange = async ({
  content,
  dispatch,
  adID,
  basicPath,
  currentMediaIndex,
  getValues,
  setValue,
}) => {
  await dispatch(
    startDeleteAdMediaID({
      adID,
      index: currentMediaIndex,
    })
  );

  const formatted = formatMediaUrl(content);
  setValue(`${basicPath}.mediaIDs.[${currentMediaIndex}]`, "");
  setValue(`${basicPath}.mediaUrls.[${currentMediaIndex}]`, formatted.url, {
    shouldValidate: true,
  });
  setValue(
    `${basicPath}.mediaData.[${currentMediaIndex}]`,
    formatted.mediaData
  );

  const mediaUrls = getValues(`${basicPath}.mediaUrls`) || [];
  return {
    mediaUrls,
    isCarousel: getValues(`${basicPath}.isCarousel`) || false,
  };
};

const applyCreativeChangeByPlatform = async (params) => {
  const { platform, content, isCarousel: isCarouselAd } = params;
  const rules = PLATFORM_MEDIA_CHANGE_RULES[platform];

  if (!rules) {
    throw new Error(
      `changeCreativeWithOptionalTextRegeneration: unsupported platform "${platform}"`
    );
  }

  const carousel = isCarouselContent(content);

  const changeResult = rules.shouldReplaceCarousel(isCarouselAd, carousel)
    ? await applyCarouselCreativeChange({
        content,
        carousel,
        dispatch: params.dispatch,
        adID: params.adID,
        basicPath: params.basicPath,
        getValues: params.getValues,
        setValue: params.setValue,
        watch: params.watch,
        rules,
      })
    : await applySingleSlotCreativeChange(params);

  if (platform === LINKEDIN) {
    return { ...changeResult, isCarousel: false };
  }

  return changeResult;
};

const buildRegenerateTextsPayload = ({
  platform,
  mediaUrls,
  isCarousel,
  getValues,
  basicPath,
  language,
  campaignContext,
  adFormat,
}) => {
  const destinationUrls = getValues(`${basicPath}.destinationUrls`) || [];

  return {
    platform,
    mediaUrls,
    isCarousel,
    language,
    campaignContext,
    adFormat,
    destinationUrls: destinationUrls.map((item) => item?.value || ""),
  };
};

const normalizeRegeneratedCopyText = (raw) => {
  if (typeof raw === "string") {
    return raw.trim();
  }
  if (raw == null) {
    return "";
  }
  if (typeof raw === "object") {
    const nested =
      raw.value ?? raw.text ?? raw.copy ?? raw.title ?? raw.headline;
    if (typeof nested === "string") {
      return nested.trim();
    }
  }
  return String(raw).trim();
};

/** Unwrap API payload whether action returns copy arrays directly or nested under `data`. */
export const unwrapRegeneratedCopyPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const hasCopyArrays = (value) =>
    ["titles", "headlines", "descriptions"].some((field) =>
      Array.isArray(value?.[field])
    );

  if (hasCopyArrays(payload)) {
    return payload;
  }

  if (hasCopyArrays(payload.data)) {
    return payload.data;
  }

  return null;
};

const buildFormTextItemsFromRegenerated = (newValues = []) =>
  newValues
    .map((raw) => normalizeRegeneratedCopyText(raw))
    .filter((text) => text.length > 0)
    .map((value) => ({
      id: getUniqueID(),
      value,
    }));

export const hasRegeneratedTexts = (regeneratedData, platform, isCarousel) => {
  if (!regeneratedData || typeof regeneratedData !== "object") {
    return false;
  }

  const { fields } = getRegeneratedTextFieldConfig(platform, isCarousel);

  return fields.some(
    (field) =>
      Array.isArray(regeneratedData[field]) &&
      regeneratedData[field].some(
        (text) => normalizeRegeneratedCopyText(text).length > 0
      )
  );
};

const applyRegeneratedTextsToForm = ({
  platform,
  basicPath,
  isCarousel,
  setValue,
  trigger,
  regeneratedData,
}) => {
  if (!hasRegeneratedTexts(regeneratedData, platform, isCarousel)) {
    return;
  }

  const { fields, triggerFields } = getRegeneratedTextFieldConfig(
    platform,
    isCarousel
  );

  const setValueOptions = {
    shouldDirty: true,
    shouldValidate: true,
    shouldTouch: true,
  };

  fields.forEach((fieldName) => {
    const newValues = regeneratedData[fieldName];
    if (!Array.isArray(newValues)) {
      return;
    }

    const formItems = buildFormTextItemsFromRegenerated(newValues);
    if (formItems.length === 0) {
      return;
    }

    const fieldPath = `${basicPath}.${fieldName}`;
    setValue(fieldPath, formItems, setValueOptions);

    formItems.forEach((item, index) => {
      setValue(`${fieldPath}.[${index}].value`, item.value, setValueOptions);
      setValue(`${fieldPath}.[${index}].id`, item.id, setValueOptions);
    });
  });

  triggerFields.forEach((fieldName) => {
    trigger?.(`${basicPath}.${fieldName}`);
  });
};

const promptRegenerateTextsAfterCreativeChange = async ({ t }) => {
  const result = await ConfirmAlert({
    title: t(CONFIRM_REGENERATE_TEXTS_AFTER_CREATIVE_CHANGE_TITLE),
    text: "",
    confirmButtonText: t(BUTTON_REGENERATE_TEXT),
    cancelButtonText: t(BUTTON_KEEP_TEXTS),
    showCancelButton: true,
    icon: "warning",
  });

  return result?.isConfirmed === true;
};

/**
 * Applies creative replacement, prompts to regenerate texts, and optionally
 * updates copy fields via backend (Meta, TikTok, LinkedIn previews).
 */
export const changeCreativeWithOptionalTextRegeneration = async ({
  platform,
  content,
  basicPath,
  adID,
  currentMediaIndex = 0,
  isCarousel = false,
  dispatch,
  getValues,
  setValue,
  watch = () => {},
  trigger = () => {},
  t,
  language,
  campaignContext = "",
  adFormat,
  creativeOptimizations,
  onRemoveCreativeOptimization = () => {},
  onTriggerGeneratePreview = () => {},
  onRegeneratingTextsChange = () => {},
  skipRegenerateTexts = false,
}) => {
  if (!SUPPORTED_PLATFORMS.includes(platform)) {
    throw new Error(
      `changeCreativeWithOptionalTextRegeneration: unsupported platform "${platform}"`
    );
  }

  try {
    const changeResult = await applyCreativeChangeByPlatform({
      platform,
      content,
      dispatch,
      adID,
      basicPath,
      currentMediaIndex,
      getValues,
      setValue,
      watch,
      isCarousel,
    });

    clearCreativeOptimizationIfMatched({
      creativeOptimizations,
      basicPath,
      onRemoveCreativeOptimization,
    });

    onTriggerGeneratePreview("content");

    if (skipRegenerateTexts) {
      return { ok: true, regeneratedTexts: false };
    }

    await delayMs(REGENERATE_TEXTS_CONFIRM_ALERT_DELAY_MS);

    const shouldRegenerate = await promptRegenerateTextsAfterCreativeChange({
      t,
    });

    if (!shouldRegenerate) {
      return { ok: true, regeneratedTexts: false };
    }

    const isCarouselForCopy =
      platform === LINKEDIN ? isCarousel : changeResult.isCarousel;

    const payload = buildRegenerateTextsPayload({
      platform,
      basicPath,
      mediaUrls: changeResult.mediaUrls,
      isCarousel: isCarouselForCopy,
      getValues,
      language,
      campaignContext,
      adFormat,
    });

    let didRegenerate = false;

    try {
      onRegeneratingTextsChange(true);

      const response = await dispatch(regenerateAdTextsFromCreative(payload));
      const regeneratedData = unwrapRegeneratedCopyPayload(response);

      didRegenerate = hasRegeneratedTexts(
        regeneratedData,
        platform,
        isCarouselForCopy
      );

      if (didRegenerate) {
        applyRegeneratedTextsToForm({
          platform,
          basicPath,
          isCarousel: isCarouselForCopy,
          setValue,
          trigger,
          regeneratedData,
        });
      }
    } finally {
      onRegeneratingTextsChange(false);
    }

    if (didRegenerate) {
      onTriggerGeneratePreview("texts");
    }

    return { ok: true, regeneratedTexts: didRegenerate };
  } catch (error) {
    console.error("changeCreativeWithOptionalTextRegeneration", error);
    return { ok: false, regeneratedTexts: false };
  }
};
